/**
 * isomorphic cookie util - please use this and only this util both on client and server
 */
import { setCookie } from 'client/utils/cookie/helpers/set-cookie';

import { getCookie } from 'client/utils/cookie/helpers/get-cookie';

/**
 * @function SetCookie
 * Sets a cookie.
 * @param name - The name of the cookie to set.
 * @param val - The value to set for the cookie.
 * @param options - Optional settings for the cookie (e.g., path, expires).
 * @param isSameSiteNoneCompatible - Flag to enable SameSite=None for compatible cookies. Not used in server context.
 */

/**
 * @function RemoveCookie
 * Removes a cookie.
 * @param name - The name of the cookie to remove.
 * @param options - The options.
 */

/**
 * @function GetCookie
 * Gets a cookie value by name. If no name is provided, it returns all cookies.
 * @example
 * const location = getCookie('location')
 * // or
 * const { location, EdmundsYear } = getCookie(); // more performant on client side than getting the two cookies one by one
 * @param {string|undefined} [name] - The name of the cookie to retrieve. If omitted, all cookies are returned.
 * @returns {string|object|undefined} The cookie value, or an object of all cookies if name is omitted, or undefined if the cookie doesn't exist.
 */

/**
 * @typedef Cookie
 * @property {GetCookie} get
 * @property {SetCookie} set
 * @property {RemoveCookie} remove
 */

/**
 * Removes a cookie from the client document by clearing it.
 * This function is intended for client-side use only.
 * @param {string} name - The name of the cookie to remove.
 * @param {object} [options] - The options.
 */
function remove(name, options = {}) {
  document.cookie = setCookie(name, '', { ...options, maxAge: -1 });
}

/**
 * Sets a cookie on the client document.
 * Handles SameSite=None compatibility for specific cookies.
 * This function is intended for client-side use only.
 * @param {string} name - The name of the cookie to set.
 * @param {string | object} val - The value to set for the cookie.
 * @param {object} [options] - Options for the cookie (e.g., path, expires).
 * @param {boolean} [isSameSiteNoneCompatible=false] - Flag to enable SameSite=None for compatible cookies.
 */
function set(name, value, options = undefined, isSameSiteNoneCompatible = undefined) {
  document.cookie = setCookie(name, value, options, isSameSiteNoneCompatible);
}

/**
 * This module provides a set of isomorphic utility functions for handling HTTP cookies.
 * It exposes a consistent, unified interface for managing cookies in both client-side (browser)
 * and server-side environments, while the underlying implementation is swapped out depending on the runtime.
 *
 * @see [Server Implementation](server/utils/cookie.js)
 * @type {Cookie}
 */
// eslint-disable-next-line no-underscore-dangle
export const cookie = global.__serverCookieUtil ?? { set, get: getCookie, remove };
